// @ts-check
/**
 * LiveRefreshBadge — a compact "🟢 Live · refreshed 12s ago" pill with
 * a manual refresh icon. Consumes the useLiveWidgets hook so multiple
 * badges on the same page share a single batched refresh cycle.
 *
 * Design decisions:
 *   • The pill renders NOTHING unless the widget has
 *     `chart_config.is_live` — inert widgets stay silent.
 *   • The elapsed-time string is derived on every render from a
 *     tick counter so "12s ago" auto-updates without re-fetching.
 *   • Manual refresh calls `refreshNow()` from the shared hook
 *     rather than firing a per-widget request — cheaper for
 *     dashboards with many live widgets.
 */
import React, { useEffect, useState } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';

function formatElapsed(ms) {
  if (ms < 0 || !Number.isFinite(ms)) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

/**
 * @param {{
 *   widget: {widget_id: string, chart_config?: {is_live?: boolean}};
 *   refreshedAt: string | null;
 *   refreshing?: boolean;
 *   onRefresh?: () => void | Promise<void>;
 *   error?: string | null;
 * }} props
 */
export function LiveRefreshBadge({ widget, refreshedAt, refreshing, onRefresh, error }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    // Re-render every 5s so the elapsed label stays fresh even when
    // no fetches are happening.
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);
  if (!widget?.chart_config?.is_live) return null;
  const elapsedLabel = refreshedAt
    ? formatElapsed(Date.now() - new Date(refreshedAt).getTime())
    : 'not yet refreshed';
  const isError = !!error;
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-medium tracking-wide uppercase transition-colors ${
        isError
          ? 'border-rose-500/50 bg-rose-500/[0.10] text-rose-200'
          : 'border-emerald-500/50 bg-emerald-500/[0.08] text-emerald-200'
      }`}
      data-testid={`live-refresh-badge-${widget.widget_id}`}
      title={isError ? `Refresh failed: ${error}` : `Last refreshed: ${refreshedAt || 'never'}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isError ? 'bg-rose-400' : 'bg-emerald-400'}`} />
      <span>Live · {elapsedLabel}</span>
      {onRefresh && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRefresh(); }}
          disabled={refreshing}
          className="ml-0.5 rounded p-0.5 hover:bg-white/[0.06] disabled:opacity-40"
          data-testid={`live-refresh-button-${widget.widget_id}`}
          title="Refresh now"
          aria-label="Refresh live data now"
        >
          {refreshing
            ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
            : <RefreshCw className="w-2.5 h-2.5" />}
        </button>
      )}
    </div>
  );
}

export default LiveRefreshBadge;
